// Copied into your app by RapidRailsUI (bin/rails rui:update).
// Don't edit this copy: the next update overwrites it. Fix it in the rapid_rails_ui gem.
//
// Pure chart geometry: no DOM. Mirrors the Ruby side of rui_chart
// (Chart::Component#nice_ticks, #slice_path, #line_path_d, #x_of, #y_of), and
// test/javascript/chart_geometry.test.mjs checks both agree on shared fixtures.

// Ticks at 1, 2, 5 times a power of ten that cover lo..hi. Exact step multiples,
// so 6000 never prints as 6,000.0.
export function niceTicks(lo, hi, target = 5) {
  const span = hi - lo
  if (span <= 0) return [lo, hi]
  const raw = span / target
  const mag = 10 ** Math.floor(Math.log10(raw))
  const f = raw / mag
  const step = ([1, 2, 5, 10].find((m) => f <= m) || 10) * mag
  const first = Math.floor(lo / step) * step
  const last = Math.ceil(hi / step) * step
  const count = Math.min(50, Math.round((last - first) / step) + 1)
  return Array.from({ length: count }, (_, i) => Number((first + i * step).toFixed(10)))
}

// Domain after an update: grows to cover new values in either direction, never
// shrinks (the projection and band stay where the server put them). Returns null
// when the current domain already covers them (0.77.1: negatives used to fall
// below the plot because only the top grew).
export function expandDomain(min, max, values) {
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length === 0) return null
  const lo = Math.min(min, ...finite)
  const hi = Math.max(max, ...finite)
  if (lo >= min && hi <= max) return null
  const ticks = niceTicks(lo, hi)
  return { min: Math.min(ticks[0], min), max: Math.max(ticks[ticks.length - 1], max), ticks }
}

// Geometry of a line or area plot from the svg's data attributes (viewBox units).
// `dataset` is the svg element's dataset (strings); `n` the category count.
export function lineGeometry(dataset, n) {
  const geo = {
    left: Number(dataset.chartLeft), right: Number(dataset.chartRight), top: Number(dataset.chartTop),
    plotHeight: Number(dataset.chartPlotHeight), width: Number(dataset.chartWidth),
    min: Number(dataset.chartDomainMin), max: Number(dataset.chartDomainMax)
  }
  geo.range = geo.max - geo.min || 1
  geo.x = (c) => (n <= 1 ? (geo.left + geo.right) / 2 : geo.left + c * ((geo.right - geo.left) / (n - 1)))
  geo.y = (v) => geo.top + (geo.max - v) / geo.range * geo.plotHeight
  return geo
}

// The line SVG's viewBox is cropped to the plot (left..width, 0..top+plotHeight)
// and stretched to the plot box, so text and points are HTML placed in percent of
// that box. Mirrors Chart::Component#left_pct and #top_pct (0.77.0).
export function plotPercent(x, y, geo) {
  const round3 = (n) => Math.round(n * 1000) / 1000
  return {
    left: round3((x - geo.left) * 100 / (geo.width - geo.left)),
    top: round3(y * 100 / (geo.top + geo.plotHeight))
  }
}

// A pointer offset in px across the plot box back to viewBox x units.
export function unitsAtX(px, boxWidth, geo) {
  return geo.left + px * (geo.width - geo.left) / (boxWidth || 1)
}

export function linePath(values, geo) {
  return values.map((v, c) => `${c === 0 ? "M" : "L"} ${geo.x(c).toFixed(2)} ${geo.y(v).toFixed(2)}`).join(" ")
}

export function areaPath(values, geo) {
  if (values.length === 0) return ""
  const base = geo.y(0).toFixed(2)
  return `${linePath(values, geo)} L ${geo.x(values.length - 1).toFixed(2)} ${base} L ${geo.x(0).toFixed(2)} ${base} Z`
}

// Donut or pie slice covering the fraction interval [a, b) of the circle, from
// 12 o'clock, with a 1 px gap. inner 0 draws a pie wedge from the centre.
export function arcPath(a, b, outer, inner, size) {
  const gap = 1 / outer / (2 * Math.PI)
  const a2 = a + gap
  const b2 = b - gap
  if (b2 <= a2) return ""
  const large = (b2 - a2) > 0.5 ? 1 : 0
  const c = size / 2
  const pt = (r, f) => [(c + r * Math.sin(f * 2 * Math.PI)).toFixed(3), (c - r * Math.cos(f * 2 * Math.PI)).toFixed(3)]
  const [o1x, o1y] = pt(outer, a2)
  const [o2x, o2y] = pt(outer, b2)
  if (inner === 0) return `M ${c} ${c} L ${o1x} ${o1y} A ${outer} ${outer} 0 ${large} 1 ${o2x} ${o2y} Z`
  const [i2x, i2y] = pt(inner, b2)
  const [i1x, i1y] = pt(inner, a2)
  return `M ${o1x} ${o1y} A ${outer} ${outer} 0 ${large} 1 ${o2x} ${o2y} L ${i2x} ${i2y} A ${inner} ${inner} 0 ${large} 0 ${i1x} ${i1y} Z`
}

// Heatmap colour strength: minPct at the minimum value, 100 at the maximum.
// Zero is a real value (lightest step when it is the minimum); nil is handled
// by the caller as "no data".
export function heatPercent(value, min, max, minPct = 8) {
  if (max === min) return minPct
  const pct = minPct + (value - min) / (max - min) * (100 - minPct)
  return Math.round(pct * 100) / 100
}

// Sparkline scale: 100 x 24 viewBox with a 2 unit vertical pad.
export function sparkGeometry({ lo, hi, n, width = 100, height = 24, pad = 2 }) {
  const top = hi === lo ? lo + 1 : hi
  return {
    lo,
    hi: top,
    x: (i) => (n <= 1 ? width / 2 : i * width / (n - 1)),
    y: (v) => pad + (top - v) / (top - lo) * (height - 2 * pad)
  }
}

// Mirrors Chart::Component#phone_tick_hidden? (fixtures/phone_ticks.json).
// Value ticks under horizontal bars are wide numbers; below 480 px about three
// fit. First and last always show, never one right before the last.
export const PHONE_VALUE_TICKS = 3

export function phoneTickHidden(i, count) {
  if (count <= PHONE_VALUE_TICKS) return false
  const step = Math.ceil(count / PHONE_VALUE_TICKS)
  const last = count - 1
  if (i === 0 || i === last) return false
  return !(i % step === 0 && last - i >= step)
}
